import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlowProvider,
  useReactFlow,
  Node,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useConversation } from '../../../contexts/ConversationContext';
import { forkLandPair, decorateForkLand } from './forkLightBoth';
import { decorateGreetingFlash } from './greetingFlash';
import { decorateDirectorSpotlight } from './directorSpotlight';
import { useCameraDirector } from '../../../contexts/CameraDirectorContext';
import { useDirectorRun } from './hooks/useCameraDirector';
import { useReplay } from '../../../contexts/ReplayContext';
import { messagesUpToStep } from './utils/replaySteps';
import { ReplayTransport } from './ReplayTransport';
import { councilWsClient } from '../../../services/councilWsClient';
import { LensContext } from '../../../contexts/LensContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTheaterMode } from '../../../contexts/TheaterModeContext';
import { useChatDagRuntime } from '../../../contexts/ChatDagRuntimeContext';
import { useChatDagDimensions } from '../../../contexts/ChatDagDimensionsContext';
import { usePersonaChatUI, type PanelHandlerRefs } from '../../../contexts/PersonaChatUIContext';
import { useToast } from '../../../contexts/ToastContext';
import { chatDagNodeTypes } from './nodes';
import PillBar from './PillBar';
import { Cockpit } from './cockpit/Cockpit';
import { chatDagEdgeTypes } from './edges';
import { messagesToDag } from './utils/messagesToDag';
import { useSpineSpawnControl } from './hooks/useSpineSpawnControl';
import { useHillTargets } from '../../../hooks/useHillTargets';
import { layoutDag } from './utils/layoutDag';
import { useAncestorMap } from './hooks/useAncestorMap';
import { useTweenedNodePositions } from './hooks/useTweenedNodePositions';
// P3 SP2 — lens ghosting. useLensSnapshot (NEVER useLens — it throws outside
// a provider) returns null on non-lens surfaces → the fast-path keeps all-on.
import { useLensSnapshot } from '../../../hooks/useLensSnapshot';
import { computeLensGhosting } from './utils/nodeLensSubject';
import { chatmapLog } from './utils/chatmapLog';
import { startChatmapVisObserver } from './utils/chatmapVisObserver';
// Trailmap L1 (Task 15) → L2 Drip 3 — /dag fetch + tile click → in-canvas expand
import { REACT_FLOW_ONLY_VISIBLE, REFLOW_FIT_PADDING, REFLOW_FIT_DURATION, MAX_MAPLIBRE_INSTANCES, PAN_FLOOR_DURATION } from './chatDagTokens';
import { useDagState } from './hooks/useDagState';
// Living Blueprint SP1 — summon binding fetch + open-in-Artifactory nav.
import { useNavigate } from 'react-router-dom';
import { artifactoryService } from '../../../services/artifactoryService';
import { SummonBlueprintModal } from '../../artifactory/SummonBlueprintModal';
import { BlueprintGenesisModal } from '../../artifactory/BlueprintGenesisModal';
import { getArtifact } from '../../../services/artifactService';
import type { ArtifactContentEntry } from './types';
import { selectMapToEvict, computePanToFit } from './utils/expansionGeometry';

// Camera-fit constants — single source of truth for all fitView() calls
// in this file. Three classes of fit:
//   USER  — user-initiated nav (panel row click, minimap AABB click,
//           "fit graph" button). Smooth + a touch of breathing room.
//   LIVE  — autofit during an in-flight turn. Same duration as USER so
//           rapid switching feels coherent; tighter padding so the live
//           work fills the frame; softened maxZoom (was 1.3 → 1.2) to
//           stop the over-punch-in jitter when a small stack starts.
//   FULL  — "fit the whole DAG" (turn end, fit-graph button). Slightly
//           tighter padding than USER so the wide frame doesn't waste
//           edge real estate.
// Inconsistent durations/padding across handlers (was 300/350/600 ms with
// 0.15/0.25/0.3 padding) was the visible-jitter source.
const USER_FIT_DURATION = 600;
const USER_FIT_PADDING = 0.22;
const USER_FIT_MAX_ZOOM = 1.1;
const LIVE_FIT_DURATION = 600;
const LIVE_FIT_PADDING = 0.3;
const LIVE_FIT_MAX_ZOOM = 1.2;
const FULL_FIT_PADDING = 0.18;

// Chat DAG — top-down temporal visualization of the session's tool
// orchestration.
//
// Each turn lays out as a small subgraph: user marker → LLM rounds
// (fanning out tool calls each round, with consult_peers fanning
// further into peers) → assistant marker. Turns chain end-to-start.
// All data comes from ConversationContext messages — no live
// WS stream needed, the Wave 6 response shape carries enough.
//
// Nodes are first-class React components (see nodes/*); anything you
// want to "hang" on the DAG (cost chips, highlight overlays, click
// handlers) lives there. The canvas itself is intentionally thin.

// Controls panel rendered inside <ReactFlow> via a <Panel> pinned
// top-right with a margin-top offset so it sits UNDER the MiniMap
// (which is top-right, 100px tall).
// Globals section:
//   ⤢ fit — ReactFlow fitView
//   [ ] collapse all phase stacks — global: every turn renders as a
//       single collapsed_phase_stack card
//   [ ] collapse all peer fans — global: every consult_peers fan
//       renders as a FanCollapsedChip
//   [x] show phase stack boxes — toggle the per-turn phase_stack_group
//       bounding-box backdrops
// Stacks section (one line per turn):
//   [x] Stack N: <auto-derived name>   🎯
//   Checkbox toggles per-turn collapse; 🎯 button fits the DAG view
//   on that stack and scrolls the chat pane to the user bubble.
interface ControlsPanelProps {
  // Living Blueprint SP1 — summon affordance (owned by ChatDagInner).
  onSummon?: () => void;
  blueprintBound?: boolean;
  // SP2b — birth a NEW empty blueprint to ideate from (not gated by
  // blueprintBound; a trail may grow several blueprints over its life).
  onNewBlueprint?: () => void;
  // G3a — register handleTarget into focusNodeRef so an Expedition Log row
  // click centers the ChatMap camera on the node (this component owns the
  // canonical handleTarget + is rendered inside ReactFlow, so fitView is live).
  handlerRefs?: PanelHandlerRefs;
  // Cockpit Slice 1 — the floating { } JSON debug button was relocated here so
  // it can't poke through the cockpit band's top-right; fires the same callback.
  onCopyDebugSnapshot?: () => void;
}

const ControlsPanel: React.FC<ControlsPanelProps> = ({ onSummon, blueprintBound, onNewBlueprint, handlerRefs, onCopyDebugSnapshot }) => {
  const { darkMode } = useTheme();
  const { state } = useConversation();
  const replay = useReplay();
  const runtime = useChatDagRuntime();
  // TH-3c (plan-triad S3) — during replay the "N stacks" badge must reflect the GROWN prefix,
  // not the full trail (else it reads e.g. "4 stacks" over a 1-exchange canvas in the money-shot).
  const sourceMessages = useMemo(
    () => (replay.active ? messagesUpToStep(replay.snapshot as any, replay.steps as any, replay.stepIndex) : state.messages),
    [replay.active, replay.stepIndex, replay.snapshot, replay.steps, state.messages],
  );
  const {
    setAllFansCollapsed,
    setShowPhaseStacks,
    togglePhaseStackCollapse,
    setAllPhaseStacksCollapsed,
    focusBubble,
  } = usePersonaChatUI();
  const { fitView } = useReactFlow();

  const [moreOpen, setMoreOpen] = useState(false);
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (moreBtnRef.current && moreBtnRef.current.contains(t)) return;
      if (popoverRef.current && popoverRef.current.contains(t)) return;
      setMoreOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [moreOpen]);

  // Derive the phase-stack list from messages: one entry per turn.
  // We keep the FULL prompt text now — truncation is CSS-driven via
  // line-clamp + linear-gradient mask so the panel can show two lines
  // of context per stack instead of a single 48-char slice.
  const stacks = useMemo(() => {
    const out: Array<{
      turnKey: string;
      turnIndex: number;
      fullText: string;
    }> = [];
    let lastUser: { id: string; content: string } | null = null;
    let idx = 0;
    for (const m of sourceMessages) {
      if (m.role === 'user') {
        lastUser = { id: m.id, content: m.content ?? '' };
      } else if (m.role === 'assistant' && lastUser) {
        const raw = lastUser.content.replace(/\s+/g, ' ').trim();
        out.push({
          turnKey: lastUser.id,
          turnIndex: idx,
          fullText: raw || '(empty prompt)',
        });
        idx += 1;
        lastUser = null;
      }
    }
    return out;
  }, [sourceMessages]);

  const handleTarget = React.useCallback(
    (turnKey: string) => {
      // Fit the DAG on this stack's node. Phase-stack-group when the
      // box is visible; otherwise the collapsed card id.
      const groupId = runtime.collapsedPhaseStackIds.has(turnKey) ||
        runtime.allPhaseStacksCollapsed
        ? `stack-${turnKey}`
        : `group-stack-${turnKey}`;
      try {
        fitView({
          nodes: [{ id: groupId }],
          padding: USER_FIT_PADDING,
          duration: USER_FIT_DURATION,
          maxZoom: USER_FIT_MAX_ZOOM,
        });
      } catch {
        // Node not rendered — no-op.
      }
      // Also scroll the chat pane to the matching user bubble.
      focusBubble(turnKey);
    },
    [
      fitView,
      focusBubble,
      runtime.collapsedPhaseStackIds,
      runtime.allPhaseStacksCollapsed,
    ],
  );

  // G3a — expose handleTarget to the Expedition Log via the handlerRefs ref slot.
  React.useEffect(() => {
    const ref = handlerRefs?.focusNodeRef;
    if (!ref) return;
    ref.current = (turnKey: string) => { handleTarget(turnKey); return true; };
    return () => { ref.current = null; };
  }, [handlerRefs, handleTarget]);

  const bg = darkMode
    ? 'bg-gray-900/95 border-gray-700 text-gray-200'
    : 'bg-white/95 border-gray-300 text-gray-700';

  return (
    <div
      className={`relative flex flex-col gap-1 px-2 py-1.5 rounded-md border shadow-md ${bg}`}
      style={{ width: 240 }}
    >
      {/* Header: count chip (mono chrome) + ⋯ overflow */}
      <div className="flex items-center justify-between">
        <span
          className={`text-[9px] font-mono uppercase tracking-widest ${
            darkMode ? 'text-emerald-400/70' : 'text-emerald-700/70'
          }`}
          title={`${stacks.length} phase ${stacks.length === 1 ? 'stack' : 'stacks'}`}
        >
          {stacks.length} {stacks.length === 1 ? 'stack' : 'stacks'}
        </span>
        <button
          type="button"
          ref={moreBtnRef}
          onClick={() => setMoreOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          title="More controls"
          className={`text-[12px] leading-none px-1.5 py-0.5 rounded transition-colors ${
            darkMode
              ? 'text-gray-300 hover:bg-white/10'
              : 'text-gray-700 hover:bg-black/5'
          }`}
        >
          ⋯
        </button>
      </div>

      {/* Globals popover, anchored under the ⋯ */}
      {moreOpen && (
        <div
          ref={popoverRef}
          role="menu"
          className={`absolute right-1 top-7 z-30 flex flex-col gap-1 px-2 py-2 rounded-md border shadow-lg text-[10px] font-mono ${bg}`}
          style={{ width: 220 }}
        >
          <button
            type="button"
            onClick={() => {
              fitView({
                padding: FULL_FIT_PADDING,
                duration: USER_FIT_DURATION,
                maxZoom: USER_FIT_MAX_ZOOM,
              });
              setMoreOpen(false);
            }}
            className={`text-left px-1 py-0.5 rounded uppercase tracking-wider text-[9px] ${
              darkMode
                ? 'text-emerald-300 hover:bg-emerald-900/30'
                : 'text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            ⤢ fit graph
          </button>
          <div className={`h-px ${darkMode ? 'bg-gray-700/70' : 'bg-gray-200'}`} />
          {/* Living Blueprint SP1 — summon an existing blueprint onto this trail.
              Disabled once one is bound (soft 1-per-trail). */}
          {onSummon && (
            <button
              type="button"
              role="menuitem"
              data-testid="summon-blueprint"
              onClick={() => { onSummon(); setMoreOpen(false); }}
              disabled={blueprintBound}
              title={blueprintBound ? 'This trail already has a blueprint' : 'Summon a blueprint onto this trail'}
              className={`flex items-center gap-1.5 text-left px-1 py-0.5 rounded uppercase tracking-wider text-[9px] disabled:opacity-40 ${
                darkMode ? 'text-amber-300 hover:bg-amber-900/30' : 'text-amber-700 hover:bg-amber-50'
              }`}
            >
              <span aria-hidden>📐</span>
              <span>{blueprintBound ? 'blueprint summoned' : 'summon blueprint'}</span>
            </button>
          )}
          {/* SP2b — birth a fresh empty blueprint and ideate it into being.
              Always enabled (distinct from summon's 1-per-trail soft cap). */}
          {onNewBlueprint && (
            <button
              type="button"
              role="menuitem"
              data-testid="new-blueprint"
              onClick={() => { onNewBlueprint(); setMoreOpen(false); }}
              title="Start a new blueprint and grow it from intent"
              className={`flex items-center gap-1.5 text-left px-1 py-0.5 rounded uppercase tracking-wider text-[9px] ${
                darkMode ? 'text-emerald-300 hover:bg-emerald-900/30' : 'text-emerald-700 hover:bg-emerald-50'
              }`}
            >
              <span aria-hidden>✨</span>
              <span>new blueprint</span>
            </button>
          )}
          <div className={`h-px ${darkMode ? 'bg-gray-700/70' : 'bg-gray-200'}`} />
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={runtime.allPhaseStacksCollapsed}
              onChange={(e) => setAllPhaseStacksCollapsed(e.target.checked)}
              className="h-3 w-3 accent-emerald-500 cursor-pointer"
            />
            <span>collapse all phase stacks</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={runtime.allFansCollapsed}
              onChange={(e) => setAllFansCollapsed(e.target.checked)}
              className="h-3 w-3 accent-emerald-500 cursor-pointer"
            />
            <span>collapse all peer fans</span>
          </label>
          {/* Trailmap L2 Drip 3 — one-shot action (no persistent "un-collapse";
              expansion is per-tile), so a button, not a toggle checkbox. */}
          <button
            type="button"
            role="menuitem"
            data-testid="collapse-all-artifacts"
            onClick={() => { runtime.collapseAllArtifacts(); setMoreOpen(false); }}
            disabled={runtime.expandedArtifactIds.size === 0}
            className={`flex items-center gap-1.5 text-left px-1 py-0.5 rounded disabled:opacity-40 ${
              darkMode ? 'hover:bg-emerald-900/30' : 'hover:bg-emerald-50'
            }`}
          >
            <span aria-hidden>▣</span>
            <span>collapse all open artifacts{runtime.expandedArtifactIds.size ? ` (${runtime.expandedArtifactIds.size})` : ''}</span>
          </button>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={runtime.showPhaseStacks}
              onChange={(e) => setShowPhaseStacks(e.target.checked)}
              className="h-3 w-3 accent-emerald-500 cursor-pointer"
            />
            <span>show phase stack boxes</span>
          </label>
          {onCopyDebugSnapshot && (
            <>
              <div className={`h-px ${darkMode ? 'bg-gray-700/70' : 'bg-gray-200'}`} />
              <button
                type="button"
                role="menuitem"
                data-testid="copy-debug-snapshot"
                onClick={() => { onCopyDebugSnapshot(); setMoreOpen(false); }}
                className={`text-left px-1 py-0.5 rounded uppercase tracking-wider text-[9px] ${
                  darkMode ? 'text-emerald-300 hover:bg-emerald-900/30' : 'text-emerald-700 hover:bg-emerald-50'
                }`}
              >
                {`{ } copy json snapshot`}
              </button>
            </>
          )}
        </div>
      )}

      {/* Stack list — pinned spine + 2-line sans-serif preview + hover chevron */}
      {stacks.length > 0 && (
        <div
          className={`flex flex-col gap-0.5 pt-1 border-t ${
            darkMode ? 'border-gray-700/70' : 'border-gray-200'
          }`}
        >
          {stacks.map((s) => {
            const isCollapsed =
              runtime.allPhaseStacksCollapsed ||
              runtime.collapsedPhaseStackIds.has(s.turnKey);
            const isViewport = runtime.viewportTurnId === s.turnKey;
            const isInFlight = runtime.inFlightTurnId === s.turnKey;
            const isError = runtime.errorTurnId === s.turnKey;
            // Color-match the row state to PhaseStackGroupNode's AABB.
            // PhaseStackGroupNode (nodes/PhaseStackGroupNode.tsx:36-50):
            //   error    → rose border + rose fill
            //   inFlight → bright emerald border + glow
            //   viewport → dim emerald border
            //   default  → gray border
            // Row mirrors via its left gutter rail + pin color so the
            // panel-side selection visually pairs with the canvas-side
            // AABB without any leader-line overlay.
            let railClass = '';
            let pinClass = '';
            let rowBgClass = '';
            if (isError) {
              railClass = darkMode ? 'bg-rose-400' : 'bg-rose-500';
              pinClass = darkMode
                ? 'bg-rose-400 ring-rose-300/40'
                : 'bg-rose-500 ring-rose-600/40';
              rowBgClass = darkMode ? 'bg-rose-950/20' : 'bg-rose-50/60';
            } else if (isInFlight) {
              railClass = darkMode ? 'bg-emerald-300' : 'bg-emerald-500';
              pinClass = darkMode
                ? 'bg-emerald-300 ring-emerald-200/50'
                : 'bg-emerald-500 ring-emerald-600/50';
              rowBgClass = darkMode ? 'bg-emerald-900/25' : 'bg-emerald-50/80';
            } else if (isViewport) {
              railClass = darkMode ? 'bg-emerald-500' : 'bg-emerald-500';
              pinClass = darkMode
                ? 'bg-emerald-400 ring-emerald-300/40'
                : 'bg-emerald-500 ring-emerald-600/40';
              rowBgClass = darkMode ? 'bg-emerald-950/20' : 'bg-emerald-50/40';
            } else {
              pinClass = darkMode
                ? 'bg-slate-500/70 ring-slate-400/30'
                : 'bg-slate-400/80 ring-slate-500/30';
              rowBgClass = 'hover:bg-white/[0.02] dark:hover:bg-white/[0.03]';
            }
            return (
              <div
                key={s.turnKey}
                data-turn-key={s.turnKey}
                className={`group relative flex items-stretch gap-2 pr-1 py-1 rounded transition-colors ${rowBgClass}`}
              >
                {/* Active gutter rail — mirrors AABB border color */}
                {(isViewport || isInFlight || isError) && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 top-1 bottom-1 w-[1.5px] rounded ${railClass}`}
                  />
                )}
                {/* Pin gutter — color mirrors AABB state */}
                <span
                  aria-hidden="true"
                  className="relative flex items-start justify-center w-3 pt-1.5 flex-shrink-0"
                >
                  <span
                    className={`block w-1.5 h-1.5 rounded-sm ring-1 ${pinClass}`}
                  />
                  {isInFlight && (
                    <span
                      aria-hidden="true"
                      className={`absolute inset-0 m-auto w-3 h-3 rounded-full animate-ping ${
                        darkMode ? 'bg-emerald-400/40' : 'bg-emerald-500/30'
                      }`}
                    />
                  )}
                </span>
                {/* Number + 2-line preview (sans-serif content; mono number) */}
                <button
                  type="button"
                  onClick={() => handleTarget(s.turnKey)}
                  title={s.fullText}
                  className={`flex-1 min-w-0 text-left ${
                    darkMode ? 'hover:text-emerald-300' : 'hover:text-emerald-700'
                  }`}
                >
                  <span className="block text-[9px] font-mono opacity-50 tabular-nums leading-none mb-0.5">
                    {s.turnIndex + 1}
                  </span>
                  <span
                    className="block font-sans text-[12px] font-medium leading-snug overflow-hidden"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maskImage:
                        'linear-gradient(to bottom, black 70%, transparent 100%)',
                      WebkitMaskImage:
                        'linear-gradient(to bottom, black 70%, transparent 100%)',
                    }}
                  >
                    {s.fullText}
                  </span>
                </button>
                {/* Hover chevron — collapse toggle */}
                <button
                  type="button"
                  onClick={() => togglePhaseStackCollapse(s.turnKey)}
                  disabled={runtime.allPhaseStacksCollapsed}
                  title={
                    runtime.allPhaseStacksCollapsed
                      ? 'Disabled while "collapse all phase stacks" is on'
                      : isCollapsed
                        ? 'Expand this stack'
                        : 'Collapse this stack'
                  }
                  aria-label={isCollapsed ? 'Expand stack' : 'Collapse stack'}
                  className={`self-start mt-1 text-[12px] leading-none w-4 h-4 flex items-center justify-center rounded transition-opacity ${
                    runtime.allPhaseStacksCollapsed
                      ? 'opacity-20 cursor-not-allowed'
                      : 'opacity-0 group-hover:opacity-60 hover:!opacity-100'
                  } ${
                    darkMode
                      ? 'text-gray-300 hover:bg-white/10'
                      : 'text-gray-700 hover:bg-black/5'
                  }`}
                >
                  {isCollapsed ? '▸' : '▾'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface ChatDagInnerProps {
  primaryPersona?: any;
  sessionId?: string;
  handlerRefs?: PanelHandlerRefs;
  greetingPending?: boolean;
}

const ChatDagInner: React.FC<ChatDagInnerProps> = ({ primaryPersona, sessionId, handlerRefs, greetingPending }) => {
  const { darkMode } = useTheme();
  const { state, pendingReplies, reloadMessages } = useConversation();
  // Reply-targeting §3 — nullable: the canvas can mount outside LensProvider.
  const lensCtx = useContext(LensContext);
  const runtime = useChatDagRuntime();
  const { dimensions } = useChatDagDimensions();
  const { fitView, getViewport, setViewport, getNodes } = useReactFlow();
  // TH-3b — the Camera Director owns the turn-end camera while `follow` is on.
  const { follow, paused, spotlightTurnKeys, setSpotlightTurnKeys, setPaused } = useCameraDirector();
  const replay = useReplay();
  // TH-3c — the reveal seam. Replay feeds a filtered prefix of the SNAPSHOT (never state.messages)
  // through the existing messagesToDag→layoutDag→tween pipeline; growth + re-flow come free.
  const sourceMessages = useMemo(
    () => (replay.active ? messagesUpToStep(replay.snapshot as any, replay.steps as any, replay.stepIndex) : state.messages),
    [replay.active, replay.stepIndex, replay.snapshot, replay.steps, state.messages],
  );
  const bufferedCount = replay.active ? Math.max(0, state.messages.length - replay.snapshot.length) : 0;

  // SP-3b — chat-with-artifact spawn lifecycle. onAsk/askInFlight/askError +
  // the F5 operator-seed reach the artifact tiles via the RuntimeState channel
  // (the processor stamps them onto each tile's data; nodes never read runtime).
  const spawnControl = useSpineSpawnControl({
    sessionId: sessionId ?? '',
    reloadMessages,
    seedCardSpineExpanded: runtime.seedCardSpineExpanded,
    fitView,
  });

  // canvasRootRef — anchor for the per-canvas [chatmap-vis] MutationObserver.
  // Attached to the wrapper <div> below; the observer watches subtree
  // .react-flow__node style.visibility transitions to surface the
  // visibility-gate bug signature. Per-mount lifecycle (no module
  // singleton) makes this HMR / Strict Mode safe.
  const canvasRootRef = useRef<HTMLDivElement | null>(null);

  // Trailmap L1 (plan Task 15) — /dag fetch lifecycle owned by hook.
  // Skeleton mode by default for first-paint topology; tiles fetch full
  // content via getArtifact on expand (rendered in-canvas, Drip 3).
  const { dag, refetch: refetchDag } = useDagState(sessionId ?? null, 'skeleton');
  // SP3a Hills — resolved-target resolver (persona→spine→trail) stamped onto node data.
  const { resolve: resolveHillTargetFn } = useHillTargets(sessionId ?? null);

  // Living Blueprint SP1 — the trail's bound manifest id. The chat-session API
  // does NOT serialize session.data, so fetch it directly; re-fetchable after a
  // summon (passed to the summon modal as onSummoned in Task 16).
  const navigate = useNavigate();
  const [blueprintManifestId, setBlueprintManifestId] = useState<string | null>(null);
  const refetchBlueprint = useCallback(() => {
    if (!sessionId) return;
    artifactoryService.getSessionBlueprint(sessionId)
      .then((r) => setBlueprintManifestId(r.manifest_id))
      .catch(() => setBlueprintManifestId(null));
  }, [sessionId]);
  useEffect(() => { refetchBlueprint(); }, [refetchBlueprint]);
  // Funnel → genesis happens in TrailheadCore's modal (a separate tree) and births
  // the blueprint server-side. Listen for its signal so the card appears without an
  // F5; the prevBpRef effect below roots the spine when the binding newly lands.
  useEffect(() => {
    const onGenerated = (e: Event) => {
      const sid = (e as CustomEvent).detail?.sessionId;
      if (!sid || sid === sessionId) { refetchBlueprint(); reloadMessages(); }
    };
    window.addEventListener('blueprint:generated', onGenerated);
    return () => window.removeEventListener('blueprint:generated', onGenerated);
  }, [sessionId, refetchBlueprint, reloadMessages]);
  // SP2a — summon also roots the blueprint's intent spine (Foreman-headed
  // messages added server-side by ensure_intent_spine). refetchBlueprint only
  // re-reads the binding (→ the card); reloadMessages re-pulls the canonical
  // message set so the new spine root + seeded peer responses render too.
  const handleSummoned = useCallback(() => {
    refetchBlueprint();
    reloadMessages();
  }, [refetchBlueprint, reloadMessages]);
  // SP-Summon: an agent-driven summon_blueprint (in a chat turn) BINDS ONLY — it
  // can't root the spine mid-turn without colliding with the turn save. When the
  // binding newly appears this session, complete the summon by rooting the intent
  // spine in a clean request (mirrors handleSummoned), then reload so it renders.
  // The route is idempotent + returns created:false when a spine already exists,
  // so loading an already-summoned session is a cheap no-op (no spurious reload).
  const prevBpRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevBpRef.current;
    prevBpRef.current = blueprintManifestId;
    if (prev === undefined) return;            // skip the initial (mount) observation
    if (!prev && blueprintManifestId) {
      artifactoryService.ensureBlueprintSpine(sessionId)
        .then((r) => { if (r?.created) reloadMessages(); })
        .catch(() => { /* best-effort; ensure-spine is idempotent */ });
    }
  }, [blueprintManifestId, sessionId, reloadMessages]);
  // Route is /artifactory/:manifestId — open with the manifest id (the onOpen arg).
  const handleOpenBlueprint = useCallback((mid: string) => { navigate(`/artifactory/${mid}`); }, [navigate]);
  const [summonOpen, setSummonOpen] = useState(false);

  // SP2b — genesis cost-gate state. genesisIntent seeds the editable modal;
  // the blueprint card opens it via the runtime onGenerateBlueprint channel.
  const [genesisModalOpen, setGenesisModalOpen] = useState(false);
  const [genesisIntent, setGenesisIntent] = useState('');
  // Birth a fresh empty seedling blueprint + root its intent spine, then re-read
  // the binding + reload messages (same shape as a summon). Surface non-409s.
  const handleNewBlueprint = useCallback(async () => {
    if (!sessionId) return;
    try {
      await artifactoryService.newBlueprint(sessionId);
      handleSummoned();
    } catch (e: any) {
      if (e?.response?.status !== 409) {
        // eslint-disable-next-line no-console
        console.error('Could not start a new blueprint', e);
        window.alert('Could not start a new blueprint. Please try again.');
      }
    }
  }, [sessionId, handleSummoned]);
  const handleGenerateBlueprint = useCallback((_mid: string, intent: string) => {
    setGenesisIntent(intent || '');
    setGenesisModalOpen(true);
  }, []);

  // L1+ Fix 3 (2026-05-12) — Refetch /dag when a new turn lands.
  // Without this, artifacts created during a turn never appear in the
  // chatmap until the operator reloads the page (caught in Alan's
  // smoke-test of session e9f22227 — artifact bound correctly in DB
  // but stale frontend dag state from initial mount couldn't see it).
  // Detects new turn by message-count increase + skips the first
  // render so useDagState's mount-fetch isn't doubled.
  const prevMsgLengthRef = useRef<number | null>(null);
  useEffect(() => {
    const len = state.messages.length;
    if (prevMsgLengthRef.current !== null && len !== prevMsgLengthRef.current) {
      refetchDag();
      // SP-Summon: a turn may have summoned a blueprint via a persona tool
      // (binds session.data server-side). Re-read the binding so the card
      // renders without a manual F5 — mirrors handleSummoned, on every turn.
      refetchBlueprint();
    }
    prevMsgLengthRef.current = len;
  }, [state.messages.length, refetchDag, refetchBlueprint]);

  // Trailmap L2 Drip 3 — canvas-local fetched artifact bodies, keyed by id.
  // Off the MessageList re-render path (spec §3.5). Refs let the click handler
  // read the cache + race-guard async writes WITHOUT widening its deps (fold
  // §B); synced post-commit (useLayoutEffect), never in the render body.
  const [artifactContent, setArtifactContent] =
    useState<ReadonlyMap<string, ArtifactContentEntry>>(() => new Map());
  const artifactContentRef = useRef(artifactContent);
  const sessionRef = useRef(sessionId);
  const dagRef = useRef(dag);
  const nonceRef = useRef<Map<string, number>>(new Map());
  useLayoutEffect(() => {
    artifactContentRef.current = artifactContent;
    sessionRef.current = sessionId;
    dagRef.current = dag;
  });
  // Drip 3 — drop the content map on conversation switch (DC-13; pairs with the
  // surface's expandedArtifactIds reset).
  useEffect(() => { setArtifactContent(new Map()); }, [sessionId]);

  // Trailmap L2 Drip 3 (DC-9) — polite aria-live copy for non-visual state
  // changes (ESC collapse-all, auto-map-eviction).
  const [liveMsg, setLiveMsg] = useState('');
  // ESC → collapse all open artifacts + announce. Lean per fold §G: no
  // focused-first DOM-walk (the per-tile × handles single-collapse and keeps
  // focus on the tile); DC-9 relaxed.
  const onCanvasKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (runtime.expandedArtifactIds.size) {
        runtime.collapseAllArtifacts();
        setLiveMsg('Collapsed all artifacts.');
      }
    },
    [runtime],
  );

  const MAP_TYPES = useMemo(() => new Set(['geojson']), []);
  const handleTileClick = React.useCallback(
    (artifactId: string) => {
      // Auto-group (DC-12): expansion only lives in a grouped dim — from flat,
      // switch to 'turn' so the expanded footprint reflows via the height-aware
      // clustering path.
      if (runtime.activeDim === 'off') runtime.setActiveDim('turn');

      const willExpand = !runtime.expandedArtifactIds.has(artifactId);
      if (willExpand) {
        // Maps WebGL cap (DC-5). Resolve type from the LIVE node list — a
        // skeleton dag can have empty artifacts (fold §C); a visible tile always
        // carries data.artifact.type. Fall back to the dag (via ref, keeps deps tight).
        const typeOf = (id: string): string | undefined =>
          ((getNodes().find((n) => n.id === `tile-${id}`)?.data as any)?.artifact?.type as string | undefined)
          ?? dagRef.current?.artifacts?.find((a) => a.id === id)?.type;
        if (MAP_TYPES.has(typeOf(artifactId) ?? '')) {
          const evict = selectMapToEvict(runtime.expandedArtifactIds, typeOf, MAP_TYPES, MAX_MAPLIBRE_INSTANCES);
          if (evict) {
            runtime.toggleArtifactExpansion(evict); // collapse → unmounts → map.remove()
            setLiveMsg(`Closed a map to free WebGL — max ${MAX_MAPLIBRE_INSTANCES} open at once.`);
          }
        }
      }

      runtime.toggleArtifactExpansion(artifactId);
      if (!willExpand) return; // collapsing — no fetch

      // Cache-check (DC-13): skip refetch only for a successful SAME-SESSION
      // entry. 'error' entries re-fetch on re-expand.
      const cached = artifactContentRef.current.get(artifactId);
      if (cached?.status === 'ok' && cached.session === sessionRef.current) return;

      // Per-id monotonic nonce + session stamp guard the async write against
      // expand→collapse→re-expand races and cross-session bleed (fold §D).
      const reqNonce = (nonceRef.current.get(artifactId) ?? 0) + 1;
      nonceRef.current.set(artifactId, reqNonce);
      const reqSession = sessionRef.current;
      setArtifactContent((prev) => new Map(prev).set(artifactId, { status: 'loading' }));
      getArtifact(artifactId)
        .then((a) =>
          setArtifactContent((prev) => {
            if (nonceRef.current.get(artifactId) !== reqNonce || sessionRef.current !== reqSession) return prev;
            return new Map(prev).set(artifactId, { status: 'ok', artifact: a, session: reqSession });
          }),
        )
        .catch(() =>
          setArtifactContent((prev) => {
            if (nonceRef.current.get(artifactId) !== reqNonce || sessionRef.current !== reqSession) return prev;
            return new Map(prev).set(artifactId, { status: 'error' });
          }),
        );
    },
    [runtime, MAP_TYPES, getNodes],
  );

  const { nodes, edges, turnBoundingBoxes } = useMemo(() => {
    const raw = messagesToDag(
      sourceMessages,   // TH-3c: replay-filtered prefix (=== state.messages when replay inactive)
      {
        inFlightTurnId: runtime.inFlightTurnId,
        viewportTurnId: runtime.viewportTurnId,
        collapsedFanIds: runtime.collapsedFanIds,
        livePhaseEvents: runtime.livePhaseEvents,
        wsLastEventMs: runtime.wsLastEventMs,
        allFansCollapsed: runtime.allFansCollapsed,
        collapsedPhaseStackIds: runtime.collapsedPhaseStackIds,
        allPhaseStacksCollapsed: runtime.allPhaseStacksCollapsed,
        expandedArtifactTurnIds: runtime.expandedArtifactTurnIds,
        activeDim: runtime.activeDim,
        expandedArtifactIds: runtime.expandedArtifactIds,
        primaryPersona,
        blueprintManifestId,
        onOpenBlueprint: handleOpenBlueprint,
        onGenerateBlueprint: handleGenerateBlueprint,
        onAskArtifact: spawnControl.onAsk,
        askInFlight: spawnControl.askInFlight,
        askError: spawnControl.askError,
        onSeedSpineExpanded: runtime.seedCardSpineExpanded,
        onToggleSpine: runtime.toggleCardSpineExpansion,
      },
      dag ?? undefined,
      handleTileClick,
      resolveHillTargetFn,
      artifactContent,
    );
    // v0.1 lab debug — dump the pipeline as strings so the console
    // doesn't collapse them as Array(N). Single-line per node makes
    // the pile-or-not visible at a glance. Remove once layout lands.
    /* eslint-disable no-console */
    const inLines = state.messages.map((m: any, i: number) => {
      const id = (m.id || '').slice(0, 8);
      const parents = (m.parent_ids || []).map((p: string) => p.slice(0, 8)).join(',');
      const isRoot = m.metadata?.is_spine_root ? ' [ROOT]' : '';
      const persona = (m.metadata?.persona_id || '').slice(0, 8);
      const len = (m.content || '').length;
      return `  ${i}: id=${id} role=${m.role} parents=[${parents}] persona=${persona} contentLen=${len}${isRoot}`;
    });
    console.log(`[dag/in] ${state.messages.length} messages:\n${inLines.join('\n')}`);

    const rawLines = raw.nodes.map((n: any) => {
      const id = n.id.slice(0, 40);
      return `  ${id.padEnd(42)} type=${(n.type || '').padEnd(20)} x=${n.position?.x ?? '?'} y=${n.position?.y ?? '?'}`;
    });
    const edgeLines = raw.edges.map((e: any) =>
      `  ${e.source.slice(0, 24).padEnd(26)} -> ${e.target.slice(0, 24)}`,
    );
    console.log(`[dag/raw] ${raw.nodes.length} nodes, ${raw.edges.length} edges:\n${rawLines.join('\n')}\n--edges--\n${edgeLines.join('\n')}`);

    const result = layoutDag(
      raw.nodes,
      raw.edges,
      dimensions,
      runtime.focalTurnId,
      runtime.inFlightStartedAtMs,
      { activeDim: runtime.activeDim, expandedCardSpineIds: runtime.expandedCardSpineIds },
    );
    // v0.2 T4.2: post-layout orphan sweep deleted. The new layoutDag
    // walker (topologyWalk) positions every reachable node and emits
    // a cycle_error decoration at the bailout point for unreachable
    // ones — no more (0,0) survivors. Multi-asst-under-one-user is
    // fixed at the round-id derivation level (T2.5 keyed on asst.id).

    const laidLines = result.nodes.map((n: any) => {
      const id = n.id.slice(0, 40);
      return `  ${id.padEnd(42)} type=${(n.type || '').padEnd(20)} x=${n.position?.x ?? '?'} y=${n.position?.y ?? '?'}`;
    });
    console.log(`[dag/laid] ${result.nodes.length} positioned nodes:\n${laidLines.join('\n')}`);
    /* eslint-enable no-console */
    let laidOut = result.nodes;
    // Controls panel: user can hide the phase_stack_group backdrops
    // without affecting the rest of the DAG. Filtered out post-layout
    // so positioning of everything else is unchanged.
    if (!runtime.showPhaseStacks) {
      laidOut = laidOut.filter((n) => n.type !== 'phase_stack_group');
    }
    return {
      nodes: laidOut,
      edges: result.edges,
      turnBoundingBoxes: result.turnBoundingBoxes,
    };
  }, [
    sourceMessages,   // TH-3c: was state.messages — the replay seam
    runtime.inFlightTurnId,
    runtime.viewportTurnId,
    runtime.collapsedFanIds,
    runtime.livePhaseEvents,
    runtime.wsLastEventMs,
    runtime.allFansCollapsed,
    runtime.showPhaseStacks,
    runtime.collapsedPhaseStackIds,
    runtime.allPhaseStacksCollapsed,
    runtime.expandedArtifactTurnIds,
    runtime.activeDim,
    runtime.expandedArtifactIds,
    runtime.expandedCardSpineIds,
    runtime.focalTurnId,
    runtime.inFlightStartedAtMs,
    dimensions,
    primaryPersona,
    resolveHillTargetFn,
    artifactContent,
    blueprintManifestId,
    handleOpenBlueprint,
    handleGenerateBlueprint,
    spawnControl.onAsk,
    spawnControl.askInFlight,
    spawnControl.askError,
    runtime.seedCardSpineExpanded,
  ]);

  // Trailmap L2 Drip 2 — Alt+1/2/3 cycle the grouping dim (L2 §5.3). Bound to
  // the window; ignores text inputs / contenteditable so the composer isn't
  // hijacked. setActiveDim is the stable context setter (T6). Alt+0/flat removed
  // in the 2026-06-14 dogfood recon (see PillBar).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.altKey || e.isComposing) return;
      if ((e.target as HTMLElement)?.closest('[contenteditable=true],input,textarea')) return;
      switch (e.key) {
        case '1': runtime.setActiveDim('turn'); break;
        case '2': runtime.setActiveDim('phase'); break;
        case '3': runtime.setActiveDim('persona'); break;
        default: return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [runtime]);

  // v0.4 Task 9 → Task 13 polish → 2026-04-24 first-turn fix: autofit
  // tracks the in-flight turn only. Viewport-driven focal changes drive
  // rich dispatch + dim resolution (Tasks 6/7/8) but do NOT camera-follow
  // — the user's own scroll is the camera command there, and forcing
  // fitView on every scroll turn-change handcuffs the view to one turn
  // at a time (the original "camera chase on scroll" regression,
  // `ccd72547`).
  //
  // Triggers (both collapse into this one effect):
  // - inFlightTurnId transition (start / end)       → snap in, or widen out
  // - in-flight turn's bounding box grows            → track the unfolding stack
  //
  // The bbox trigger fixes the first-turn-of-a-new-chat case: on initial
  // mount the DAG has maybe one node (user marker) when inFlight starts;
  // fitView locks to that single node and the camera froze top-left as
  // rounds/tools/peers streamed in. Bucketing bbox to 4px suppresses
  // jitter from micro-layout churn; fitView fires only on meaningful
  // growth, not every render.
  //
  // cameraBusy still fires on transitions so the tween hook switches
  // arrivals to fade-only while the fitView tween runs.
  const inFlightBBoxKey = useMemo(() => {
    const id = runtime.inFlightTurnId;
    if (!id) return null;
    const b = turnBoundingBoxes.get(id);
    if (!b) return null;
    // Bucket size raised 4 → 48. The old 4px bucket made the key change
    // on nearly every speculative node addition during a multi-tool
    // multi-peer phase. Each change refired fitView, each fitView
    // started a 600ms tween, and rapid phase events (consult_peers can
    // fan 4-8 peers in <1s) interrupted each other. Camera oscillated;
    // nodes appeared/disappeared mid-tween. 48px ≈ a small node's
    // height, so the key changes only when the bbox grows by a
    // visually meaningful step.
    return `${Math.round(b.w / 48)}x${Math.round(b.h / 48)}`;
  }, [runtime.inFlightTurnId, turnBoundingBoxes]);

  // Cold-mount refit trigger — when no inFlight turn (refreshed older
  // session), inFlightBBoxKey is null and the fitView useEffect would
  // never refire after nodes stream in. ReactFlow's
  // onlyRenderVisibleElements=true then culls everything off-screen and
  // the canvas looks empty. BINARY transition trigger: 'empty' →
  // 'populated' fires one refit; subsequent growth is handled by
  // inFlightBBoxKey (for in-flight turn) or by user scroll. A more
  // granular key (exact count or bucketed AABB) caused refit storms
  // during speculative emission (every chat_tool_invoked / peer_call
  // phase event added a node, churning the key, queueing fitViews
  // that overlapped each other's tween durations — camera never
  // settled, user saw empty canvas).
  const fullDagBBoxKey = useMemo(
    () => (nodes.length === 0 ? 'empty' : 'populated'),
    [nodes.length === 0],
  );

  const [cameraBusy, setCameraBusy] = useState(false);
  // Drip 3 pan floor reads the LIVE cameraBusy inside its bounded poll (reading
  // the state value there would be a stale closure); mirror to a ref, synced
  // post-commit so the poll always sees the current camera state.
  const cameraBusyRef = useRef(cameraBusy);
  useLayoutEffect(() => { cameraBusyRef.current = cameraBusy; });
  // Turn-end widen retry chain (leg B below) — cleared on effect re-run so a
  // new round never races a stale retry.
  const widenRetryRef = useRef<number | null>(null);
  useEffect(() => {
    // TH-3c (plan-triad B1/B2 + architect N1): while replay owns the camera, the entire live/turn-end
    // fit effect yields — a buffered live turn must not move the camera OR flip cameraBusy (which would
    // degrade the replay tween). One top-return covers the in-flight leg, the turn-end widen, and busy.
    if (replay.active) return;
    const id = runtime.inFlightTurnId;
    setCameraBusy(true);
    chatmapLog('cam', 'cameraBusy=true', { inFlightTurnId: id });
    // Debounce 100ms — collapses peer-fan bursts (~4 events in <200ms)
    // into one refit, but short enough that the camera keeps tracking
    // when phase events stream continuously (chat_peer_chunk during
    // peer thinking can fire every ~150-300ms). The earlier 250ms
    // value never expired during sustained streams; camera stayed on
    // the initial trail-head/user_marker fit and ReactFlow's
    // onlyRenderVisibleElements culled the off-viewport speculative
    // content — operator saw "blank canvas during execution".
    const h = window.setTimeout(() => {
      try {
        if (id) {
          // Always include the trail head marker + the in-flight user
          // turn_marker in the fit, even if they're outside the strict
          // liveNodes filter. Without this, the camera zooms to the
          // growing in-flight bbox and the trail head/user_marker
          // drift around the viewport as the bbox shifts — operator
          // perceives "user message floating".
          const liveNodes = nodes.filter((n) => {
            if (n.type === 'trail_head_marker') return true;
            if (n.id === `user-${id}`) return true;
            return (n.data as any)?.turnKey === id;
          });
          if (liveNodes.length > 0) {
            chatmapLog('cam', 'fitView·live-nodes', {
              targets: liveNodes.length,
              padding: LIVE_FIT_PADDING,
              duration: LIVE_FIT_DURATION,
            });
            fitView({
              nodes: liveNodes.map((n) => ({ id: n.id })),
              padding: LIVE_FIT_PADDING,
              duration: LIVE_FIT_DURATION,
              maxZoom: LIVE_FIT_MAX_ZOOM,
            });
          } else {
            chatmapLog('cam', 'fitView·empty-live-fallback', {
              padding: FULL_FIT_PADDING,
              duration: LIVE_FIT_DURATION,
            });
            fitView({
              padding: FULL_FIT_PADDING,
              duration: LIVE_FIT_DURATION,
              maxZoom: USER_FIT_MAX_ZOOM,
            });
          }
        } else {
          // TH-3b — while `follow`, the Camera Director owns the turn-end camera
          // (frames the latest exchange cluster on quiescence). Suppress the generic
          // full-DAG widen so the two don't fight. Only the turn-end leg yields; the
          // in-flight live-fit above still runs during streaming.
          if (follow) return;
          // Turn ended — widen to the full DAG. Leg B of the stuck-camera
          // field bug (2026-06-12): RF11 fitView returns FALSE (silent
          // no-op) when freshly-mounted nodes from the end-of-turn relayout
          // haven't been measured yet — the camera then stays framed on the
          // finished turn and everything else sits off-frame "disappeared".
          // Retry briefly until it takes; hold cameraBusy through the retry
          // chain so the scroll-anchor (leg A) keeps yielding to the tween.
          const WIDEN_RETRY_MS = 250;
          const WIDEN_MAX_TRIES = 8;
          const tryWiden = (attempt: number) => {
            let ok = false;
            try {
              ok = fitView({
                padding: FULL_FIT_PADDING,
                duration: LIVE_FIT_DURATION,
                maxZoom: USER_FIT_MAX_ZOOM,
              });
            } catch {
              return; /* unmounted */
            }
            chatmapLog('cam', 'fitView·turn-end-widen', { attempt, ok });
            if (ok) {
              setCameraBusy(true);
              widenRetryRef.current = window.setTimeout(() => {
                setCameraBusy(false);
                chatmapLog('cam', 'widen-settled', getViewport() as unknown as Record<string, unknown>);
              }, LIVE_FIT_DURATION + 80);
            } else if (attempt < WIDEN_MAX_TRIES) {
              setCameraBusy(true);
              widenRetryRef.current = window.setTimeout(
                () => tryWiden(attempt + 1),
                WIDEN_RETRY_MS,
              );
            } else {
              setCameraBusy(false);
            }
          };
          tryWiden(1);
        }
      } catch {
        /* unmounted */
      }
    }, 100);
    const t = window.setTimeout(
      () => {
        setCameraBusy(false);
        chatmapLog('cam', 'cameraBusy=false');
      },
      LIVE_FIT_DURATION + 80,
    );
    return () => {
      window.clearTimeout(h);
      window.clearTimeout(t);
      if (widenRetryRef.current != null) {
        window.clearTimeout(widenRetryRef.current);
        widenRetryRef.current = null;
      }
    };
    // `nodes` is intentionally not a dep — that would refire on every
    // position update. Instead we gate on inFlightBBoxKey (bucketed
    // in-flight turn AABB) AND fullDagBBoxKey (bucketed full-DAG AABB
    // + node count, for cold-mount of no-active-turn sessions).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.inFlightTurnId, inFlightBBoxKey, fullDagBBoxKey, fitView, follow, replay.active]);

  // Trailmap L2 Drip 2 — re-fit the camera when the grouping dim changes so the
  // new clusters land in view (tiles otherwise relocate off-viewport). Reuses
  // the RF11 silent-false retry + cameraBusy coordination of the turn-end widen.
  const didMountDimRef = useRef(false);
  useEffect(() => {
    if (replay.active) return;   // TH-3c (plan-triad S1): dim-switch via 1/2/3 hotkeys must not fight the replay camera
    if (!didMountDimRef.current) { didMountDimRef.current = true; return; } // skip initial mount
    const WIDEN_RETRY_MS = 250;
    const WIDEN_MAX_TRIES = 8;
    const fitDimSwitch = (attempt: number) => {
      let ok = false;
      try {
        ok = fitView({ padding: REFLOW_FIT_PADDING, duration: REFLOW_FIT_DURATION, maxZoom: USER_FIT_MAX_ZOOM });
      } catch { return; }
      if (ok) {
        setCameraBusy(true);
        widenRetryRef.current = window.setTimeout(() => setCameraBusy(false), REFLOW_FIT_DURATION + 80);
      } else if (attempt < WIDEN_MAX_TRIES) {
        setCameraBusy(true);
        widenRetryRef.current = window.setTimeout(() => fitDimSwitch(attempt + 1), WIDEN_RETRY_MS);
      } else {
        setCameraBusy(false);
      }
    };
    const h = window.setTimeout(() => fitDimSwitch(1), 60); // let layout settle first
    return () => {
      window.clearTimeout(h);
      // Drip 2 (sonnet-triad fix) — also cancel any in-flight retry chain; a rapid
      // dim re-switch otherwise leaves a stale widenRetryRef timer that fires
      // setCameraBusy(false) mid-fit. Mirrors the turn-end widen cleanup (:752-759).
      if (widenRetryRef.current != null) {
        window.clearTimeout(widenRetryRef.current);
        widenRetryRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.activeDim, fitView, replay.active]);

  // v0.4 Task 11: scroll anchor. When the focal turn's bounding
  // box shifts between layouts (e.g. a new tool call landed in an
  // earlier turn and pushed this one down), shift the viewport so
  // the focal turn stays pinned to the same screen position. Without
  // this, the user's camera jumps every time a new structural
  // change ripples through.
  //
  // NOTE: delta signs may need flipping based on ReactFlow viewport
  // orientation — verify in browser; architect A4' licenses a fix
  // here in-scope if inverted.
  const prevBoundsRef = useRef<Map<
    string,
    { x: number; y: number; w: number; h: number }
  > | null>(null);
  useLayoutEffect(() => {
    // TH-3c (plan-triad S1): replay owns the camera — the scroll-anchor must yield. Refresh the
    // bounds snapshot so no stale delta fires the instant replay exits.
    if (replay.active) { prevBoundsRef.current = new Map(turnBoundingBoxes); return; }
    // While a fitView tween owns the camera (cameraBusy — live-fit or the
    // turn-end widen), the anchor must YIELD: setViewport() here interrupts
    // the d3 transition mid-flight, so the widen never landed and the camera
    // stayed framed on the finished turn — camps/fork-dashes sat off-frame
    // "disappeared" until F5 (field bug 2026-06-12, e2e camera-widen smoke).
    // Still refresh the bounds snapshot so no stale delta fires post-tween.
    if (cameraBusy) {
      prevBoundsRef.current = new Map(turnBoundingBoxes);
      return;
    }
    const focal = runtime.focalTurnId;
    if (focal == null || prevBoundsRef.current == null) {
      prevBoundsRef.current = new Map(turnBoundingBoxes);
      return;
    }
    const prev = prevBoundsRef.current.get(focal);
    const curr = turnBoundingBoxes.get(focal);
    if (!prev || !curr) {
      prevBoundsRef.current = new Map(turnBoundingBoxes);
      return;
    }
    const deltaX = curr.x - prev.x;
    const deltaY = curr.y - prev.y;
    if (Math.abs(deltaY) < 2 && Math.abs(deltaX) < 2) {
      prevBoundsRef.current = new Map(turnBoundingBoxes);
      return;
    }
    try {
      const vp = getViewport();
      setViewport({
        x: vp.x - deltaX * vp.zoom,
        y: vp.y - deltaY * vp.zoom,
        zoom: vp.zoom,
      });
    } catch {
      /* unmounted */
    }
    prevBoundsRef.current = new Map(turnBoundingBoxes);
  }, [
    cameraBusy,
    turnBoundingBoxes,
    runtime.focalTurnId,
    getViewport,
    setViewport,
    replay.active,
  ]);

  // L2 Task 0.6 (`0be32e1b`): handbrake removed. Hook repaired to drop
  // the useMemo cache bug (RAF forceTick now picks up the new
  // performance.now() each render) and exposes isTweeningRef for
  // Phase 4 AutoNavController. ADD lift-in arrival from ancestor +
  // smoothstep cancel-restart on target change + REMOVE registry
  // cleanup preserved.
  const ancestorMap = useAncestorMap(edges);
  const { tweenedNodes, isTweeningRef } = useTweenedNodePositions(
    nodes,
    ancestorMap,
    cameraBusy,
  );

  // Trailmap L2 Drip 3 (DC-14) — pan floor: after a NEW expansion, once the
  // reflow tween + any camera tween settle, do a ONE-SHOT pan (zoom unchanged)
  // so the just-opened tile is on-screen. Bounded poll keyed on
  // expandedArtifactIds ONLY (fold §A): gate on the LIVE isTweeningRef /
  // cameraBusy (a cameraBusy-only gate silently no-ops mid-stream; setViewport
  // mid-tween kills it), read LIVE positions via getNodes() (displayNodes is
  // pre-reflow), cap ~12 ticks (~1s) then give up.
  const prevExpandedSizeRef = useRef(0);
  useEffect(() => {
    if (replay.active) return;   // TH-3c (plan-triad S1): drip pan-floor yields to the replay camera
    const size = runtime.expandedArtifactIds.size;
    const grew = size > prevExpandedSizeRef.current;
    prevExpandedSizeRef.current = size;
    if (!grew) return;
    const justOpened = [...runtime.expandedArtifactIds].pop();
    if (!justOpened) return;

    let ticks = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tryPan = () => {
      if ((isTweeningRef.current || cameraBusyRef.current) && ticks < 12) {
        ticks += 1;
        timer = setTimeout(tryPan, 80);
        return;
      }
      const node = getNodes().find((n) => n.id === `tile-${justOpened}`);
      const wrap = canvasRootRef.current?.getBoundingClientRect();
      if (!node || !wrap) return;
      const tile = {
        x: node.position.x,
        y: node.position.y,
        w: node.width ?? 600,
        h: node.height ?? 640,
      };
      const next = computePanToFit(
        getViewport(),
        tile,
        { width: wrap.width, height: wrap.height },
        24,
      );
      if (next) setViewport(next, { duration: PAN_FLOOR_DURATION });
    };
    timer = setTimeout(tryPan, 80);
    return () => { if (timer) clearTimeout(timer); };
  }, [runtime.expandedArtifactIds, getNodes, getViewport, setViewport, isTweeningRef, replay.active]);

  // P3 SP2 — map reflects the lens. Visibility over PRE-tween nodes (stable
  // across animation frames); stamping over tweenedNodes. Identity fast-path
  // when nothing is off, so RF sees the exact same arrays outside a lens.
  const lensSnapshot = useLensSnapshot();
  const { offNodeIds, offEdgeIds } = useMemo(
    () => computeLensGhosting(nodes, edges, lensSnapshot),
    [nodes, edges, lensSnapshot],
  );
  // Reply-targeting §3 — ONE decoration pass (plan-triad): off-lens ghosting +
  // cursor outline + camp pending re-arm share this memo so the identity
  // fast-path (RF sees the exact same array when nothing decorates) survives.
  const cursorTurnId = lensCtx?.cursorTurnId ?? null;
  const pendingSpineIds = useMemo(
    () => new Set((pendingReplies ?? []).map((p) => p.spineRootMessageId)),
    [pendingReplies],
  );
  // §3 Task 8 — light BOTH branches while a fork is in-flight (FR + the trunk
  // continuation), so the operator doesn't lose the original path on fork-land.
  const forkPair = useMemo(
    () => forkLandPair(state.messages as any, pendingSpineIds),
    [state.messages, pendingSpineIds],
  );
  // TH-1 — the greeting child-turn id to flash (identity-gated to the sherpa,
  // one-shot). greetingDoneRef latches so a human-races-first turn or a remount
  // can't re-arm the flash + camera-yank the wrong turn.
  const [greetingFlashId, setGreetingFlashId] = useState<string | null>(null);
  const greetingDoneRef = useRef(false);
  const displayNodes = useMemo(() => {
    if (offNodeIds.size === 0 && !cursorTurnId && pendingSpineIds.size === 0
        && !forkPair.forkRootId && !forkPair.trunkContinuationId && !greetingFlashId
        && !spotlightTurnKeys && !replay.active) {
      return tweenedNodes;  // identity fast-path
    }
    const decorated = tweenedNodes.map((n) => {
      let out = n;
      if (offNodeIds.has(out.id)) {
        out = { ...out, className: `${out.className ?? ''} chatmap-offlens`.trim(),
                data: { ...out.data, offLens: true } };
      }
      const mid = (out.data as any)?.messageId ?? (out.data as any)?.spineRootMessageId;
      if (cursorTurnId && mid === cursorTurnId) {
        out = { ...out, className: `${out.className ?? ''} chatmap-reply-cursor`.trim() };
      }
      // Camp re-arm: campArrivalState is permanently 'resolved' once any peer
      // child exists — while a reply is pending on this spine, show the
      // existing "reading the trail…" chrome again. Reload self-heals.
      if (out.type === 'spine_root_marker'
          && pendingSpineIds.has((out.data as any)?.spineRootMessageId)
          && (out.data as any)?.arrivalState === 'resolved') {
        out = { ...out, data: { ...out.data, arrivalState: 'pending' } };
      }
      return out;
    });
    // TH-3c — under replay suppress greeting + fork decorators; ONLY the director rings the cluster.
    const withFork = replay.active ? decorated : decorateForkLand(decorated, forkPair);
    const withGreeting = replay.active ? withFork : decorateGreetingFlash(withFork, greetingFlashId);
    return decorateDirectorSpotlight(withGreeting, spotlightTurnKeys);
  }, [tweenedNodes, offNodeIds, cursorTurnId, pendingSpineIds, forkPair, greetingFlashId, spotlightTurnKeys, replay.active]);
  const displayEdges = useMemo(() => {
    if (offEdgeIds.size === 0) return edges;
    return edges.map((e) =>
      offEdgeIds.has(e.id) ? { ...e, className: 'chatmap-offlens' } : e);
  }, [edges, offEdgeIds]);

  // TH-1 — widen the camera to the full DAG when the greeting lands. Mirrors the
  // turn-end widen's RF11 silent-false retry chain (a freshly-mounted unmeasured
  // node makes fitView return false); holds cameraBusy through the retries.
  // TH-3b — ONE fit-with-retry for greeting-widen AND the Camera Director. RF11 fitView
  // returns FALSE (silent no-op) when freshly-mounted nodes aren't measured yet; retry
  // briefly until it takes, holding cameraBusy through the chain so the scroll-anchor
  // keeps yielding to the tween. Behavior-preserving extraction of tryWidenFromGreeting.
  const fitWithRetry = useCallback((opts: { nodes?: { id: string }[]; padding?: number; duration?: number; maxZoom?: number }) => {
    const RETRY_MS = 250, MAX_TRIES = 8;
    const dur = opts.duration ?? LIVE_FIT_DURATION;
    const attempt = (n: number) => {
      let ok = false;
      try {
        ok = fitView(opts as any);
      } catch {
        return; /* unmounted */
      }
      if (ok) {
        setCameraBusy(true);
        widenRetryRef.current = window.setTimeout(() => setCameraBusy(false), dur + 80);
      } else if (n < MAX_TRIES) {
        setCameraBusy(true);
        widenRetryRef.current = window.setTimeout(() => attempt(n + 1), RETRY_MS);
      } else {
        setCameraBusy(false);
      }
    };
    attempt(1);
    // closes over stable setters/refs + fitView
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitView]);

  const tryWidenFromGreeting = useCallback(() => {
    fitWithRetry({ padding: FULL_FIT_PADDING, duration: LIVE_FIT_DURATION, maxZoom: USER_FIT_MAX_ZOOM });
  }, [fitWithRetry]);

  // TH-3b — the newest turn's anchor turnKey: the last node carrying isLatestTurn,
  // else the last node's turnKey (covers the greeting-only cold graph).
  const latestAnchor = useMemo(() => {
    const latest = nodes.filter((n) => (n.data as any)?.isLatestTurn);
    const pick = latest[latest.length - 1] ?? nodes[nodes.length - 1];
    return (pick?.data as any)?.turnKey ?? null;
  }, [nodes]);
  // TH-3c — replay routes through the ONE framing path: feed the current step's cluster anchor and
  // force the director to run (follow || replay.active); suppress the live pan-grace pause while replay.
  const replayAnchor = replay.active && replay.steps.length
    ? replay.steps[Math.min(replay.stepIndex, replay.steps.length - 1)].anchor : null;
  const directorAnchor = replay.active ? replayAnchor : latestAnchor;
  useDirectorRun({ follow: follow || replay.active, paused: paused && !replay.active,
                   nodes, latestAnchor: directorAnchor, fitWithRetry, setSpotlightTurnKeys });

  // TH-3b grace (D7) — when the user manually pans while following, relax the lighting
  // and pause; re-enable after 4s (useDirectorRun only reframes on a NEW turn, so this
  // never yanks the camera back). RF11 fires onMoveStart for programmatic fitView too
  // (event === null for those) — gate on the event so the director's own reframe is ignored.
  const graceTimer = useRef<number | null>(null);
  const onDirectorMoveStart = useCallback((e: MouseEvent | TouchEvent | null) => {
    if (!e || !follow) return;
    setPaused(true);
    setSpotlightTurnKeys(null);
    if (graceTimer.current) window.clearTimeout(graceTimer.current);
    graceTimer.current = window.setTimeout(() => setPaused(false), 4000);
  }, [follow, setPaused, setSpotlightTurnKeys]);

  // TH-1 — flash + widen when the sherpa's greeting turn lands. Identity-gated to
  // the sherpa (R3): a human-races-first turn or a remount re-arm must NOT flash.
  // One-shot via greetingDoneRef. WS frames arrive raw with a top-level
  // event_type + source_persona_id (auto-subscribed for every PeerEventType).
  useEffect(() => {
    if (!sessionId) return;
    return councilWsClient.subscribe(sessionId, (frame: any) => {
      if (frame?.event_type === 'turn_added' && !greetingDoneRef.current
          && String(frame.source_persona_id) === String(primaryPersona?.id)) {
        greetingDoneRef.current = true;
        setGreetingFlashId(frame.target?.message_id ?? null);
        tryWidenFromGreeting();
      }
    });
  }, [sessionId, primaryPersona, tryWidenFromGreeting]);

  // TH-1 (R2) — connect-race catch-up: the greeting may have already arrived
  // before the subscribe above attached. If a sherpa-authored turn node is
  // already on the canvas while greetingPending is unresolved, flash + widen it.
  useEffect(() => {
    if (!greetingPending || greetingDoneRef.current || !primaryPersona?.id) return;
    const hit = nodes.find((n) =>
      n.type !== 'spine_root_marker'
      && String((n.data as any)?.personaId) === String(primaryPersona.id)
      && (n.data as any)?.messageId);
    if (hit) {
      greetingDoneRef.current = true;
      setGreetingFlashId((hit.data as any).messageId);
      tryWidenFromGreeting();
    }
  }, [greetingPending, nodes, primaryPersona, tryWidenFromGreeting]);

  // TH-3a — entering/leaving Theater Mode resizes the canvas (the app chrome is
  // reclaimed). Re-frame the DAG to the new stage once per transition, reusing the
  // greeting widen's fit + RF11 retry chain. Skip the initial mount so it doesn't
  // fight the mount fit. (Seam the Camera Director (TH-3b) plugs into.)
  const { theater } = useTheaterMode();
  const theaterPrevRef = useRef<boolean | null>(null);
  useEffect(() => {
    // TH-3c (plan-triad S1): replay's OWN auto-theater enter must NOT double-fit against the
    // director's step-0 cluster frame. Track the transition but let the director own the camera.
    if (theaterPrevRef.current === null) { theaterPrevRef.current = theater; return; }
    if (theaterPrevRef.current !== theater) {
      theaterPrevRef.current = theater;
      if (replay.active) return;
      // chrome reclaim is a layout reflow → defer a frame, then fit with retry.
      requestAnimationFrame(() => tryWidenFromGreeting());
    }
  }, [theater, tryWidenFromGreeting, replay.active]);

  // Stable fitViewOptions — an inline object would re-fit the camera on every
  // ChatDagCanvas re-render. Memoize so RF's fitView fires once on mount.
  const fitViewOptionsStable = useMemo(
    () => ({ padding: FULL_FIT_PADDING, maxZoom: USER_FIT_MAX_ZOOM }),
    [],
  );

  const isEmpty = nodes.length === 0;

  // Debug snapshot button — emits a schema-versioned JSON blob with
  // nodes, edges, runtime, flag state, and a trimmed recent-messages
  // tail so we can paste it back into Claude when iterating on DAG
  // behavior. Added after the "DAG sat still, then snapped to full"
  // live-update bug investigation.
  const { showToast } = useToast();
  const handleCopyDebugSnapshot = React.useCallback(async () => {
    const recent = state.messages.slice(-6).map((m) => ({
      id: m.id,
      role: m.role,
      timestamp: m.timestamp,
      snippet: (m.content ?? '').slice(0, 120),
      toolCallCount: Array.isArray((m.metadata as any)?.tool_calls)
        ? (m.metadata as any).tool_calls.length
        : 0,
    }));
    const payload = {
      schema: 'chatdag-debug-snapshot-v1',
      capturedAt: new Date().toISOString(),
      counts: {
        nodes: nodes.length,
        edges: edges.length,
        messages: state.messages.length,
        livePhaseEvents: runtime.livePhaseEvents.length,
      },
      runtime: {
        inFlightTurnId: runtime.inFlightTurnId,
        viewportTurnId: runtime.viewportTurnId,
        errorTurnId: runtime.errorTurnId,
        collapsedFanIds: Array.from(runtime.collapsedFanIds),
        allFansCollapsed: runtime.allFansCollapsed,
        showPhaseStacks: runtime.showPhaseStacks,
        collapsedPhaseStackIds: Array.from(runtime.collapsedPhaseStackIds),
        allPhaseStacksCollapsed: runtime.allPhaseStacksCollapsed,
        wsLastEventMs: runtime.wsLastEventMs,
        wsLastEventAgoMs:
          runtime.wsLastEventMs != null
            ? Date.now() - runtime.wsLastEventMs
            : null,
      },
      recentMessages: recent,
      nodes,
      edges,
      livePhaseEvents: runtime.livePhaseEvents,
    };
    const json = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      showToast(
        `DAG snapshot copied (${nodes.length}n/${edges.length}e, ${runtime.livePhaseEvents.length} live events).`,
        'success',
      );
    } catch {
      // Some browsers (non-secure contexts) block clipboard writes.
      // Fallback: open a small window so the user can copy manually.
      const w = window.open('', '_blank', 'width=720,height=520');
      if (w) {
        w.document.title = 'ChatMap debug snapshot';
        w.document.body.style.cssText = 'margin:0;padding:8px;font-family:monospace;font-size:11px;white-space:pre-wrap;background:#0b1220;color:#e5e7eb';
        w.document.body.textContent = json;
      } else {
        showToast('Clipboard blocked — check browser permissions.', 'error');
      }
    }
  }, [
    nodes,
    edges,
    runtime.inFlightTurnId,
    runtime.viewportTurnId,
    runtime.errorTurnId,
    runtime.collapsedFanIds,
    runtime.livePhaseEvents,
    runtime.wsLastEventMs,
    state.messages,
    showToast,
  ]);

  // Canvas-level click router (RF gotcha #1). NOTE (2026-06-12, plan-triad
  // verified): PeerResponseNode + byKind turn nodes add NO onClick/
  // stopPropagation, and SpineRootMarkerNode only stops propagation on its
  // foldback BUTTON — node-body clicks DO bubble here. Routed kinds:
  // phase_stack_group (focus bubble), artifact_tile (L2 Task 0.4), and
  // peer_response / spine_root_marker (reply cursor). Tile keyboard
  // activation (Enter/Space) stays in the tile node since ReactFlow
  // doesn't dispatch keys at canvas-level.
  const { focusBubble } = usePersonaChatUI();
  const handleNodeClick = React.useCallback(
    (_e: React.MouseEvent, node: Node) => {
      if (node.type === 'phase_stack_group') {
        const turnKey = (node.data as any)?.turnKey as string | undefined;
        if (turnKey) focusBubble(turnKey);
        return;
      }
      if (node.type === 'artifact_tile') {
        const artifactId = (node.data as any)?.artifact?.id as string | undefined;
        if (artifactId) handleTileClick(artifactId);
        return;
      }
      // Reply-targeting §3 — node click sets/toggles the reply cursor.
      if (node.type === 'peer_response' || node.type === 'spine_root_marker') {
        const mid = ((node.data as any)?.messageId
          ?? (node.data as any)?.spineRootMessageId) as string | undefined;
        if (mid && lensCtx) {
          lensCtx.setLens({
            cursorTurnId: lensCtx.cursorTurnId === mid ? null : mid,
          });
        }
        return;
      }
    },
    [focusBubble, handleTileClick, lensCtx],
  );

  // [chatmap-vis] observer mount — per-canvas-instance, no module
  // singleton. Cleanup disconnects on unmount; HMR / Strict Mode dev
  // double-invoke run mount→cleanup→mount and each pair is fully
  // symmetric. The helper itself early-returns when
  // localStorage.chatmapDebug is not '1' — zero overhead off.
  // Debug toggle requires reload to flip (operator path).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!canvasRootRef.current) return;
    return startChatmapVisObserver(canvasRootRef.current);
  }, []);

  // [chatmap-round] round boundaries — inFlightTurnId null↔id.
  useEffect(() => {
    chatmapLog(
      'round',
      runtime.inFlightTurnId ? 'round-start' : 'round-end',
      { id: runtime.inFlightTurnId },
    );
  }, [runtime.inFlightTurnId]);

  // [chatmap-cam] inFlightBBoxKey transitions — bucket key changes
  // (live-fit refit trigger). Useful for diagnosing camera oscillation.
  useEffect(() => {
    chatmapLog('cam', 'inFlightBBoxKey-change', { key: inFlightBBoxKey });
  }, [inFlightBBoxKey]);

  return (
    <div
      ref={canvasRootRef}
      onKeyDown={onCanvasKeyDown}
      className={`h-full w-full relative ${darkMode ? 'bg-gray-950/40 chatdag-dark' : 'bg-gray-50'}`}
    >
      {/* Trailmap L2 Drip 3 (DC-9) — polite live region for non-visual state. */}
      <div aria-live="polite" className="sr-only" data-testid="chatmap-live">{liveMsg}</div>
      {isEmpty && !greetingPending && (
        <div className="absolute inset-0 flex items-center justify-center text-xs opacity-60 pointer-events-none">
          No turns yet — send a message to see the DAG light up.
        </div>
      )}
      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        nodeTypes={chatDagNodeTypes}
        edgeTypes={chatDagEdgeTypes}
        onlyRenderVisibleElements={REACT_FLOW_ONLY_VISIBLE}
        fitView
        fitViewOptions={fitViewOptionsStable}
        proOptions={{ hideAttribution: true }}
        // Low static outer-zoom floor (operator ask 2026-06-15): wheel out far
        // enough to see the ENTIRE canvas. 0.05 fits a ~18000px-tall canvas in a
        // ~900px pane — huge margin over real sessions (a86f58b5 expanded ≈
        // 4988px → needs ~0.18). A per-canvas DYNAMIC floor was tried but RF
        // re-binds d3-zoom whenever the minZoom prop changes, dropping the wheel
        // (the "dead zone" regression) — static is the robust choice.
        minZoom={0.05}
        maxZoom={1.75}
        defaultEdgeOptions={{
          type: 'default', // bezier — fans read as fans (Alan's v0.2 ask)
          animated: true,  // marching-ants flow direction
          style: {
            strokeWidth: 2,
            stroke: darkMode ? '#10b981' : '#059669',
            strokeOpacity: 0.85,
          },
        }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        panOnDrag
        zoomOnScroll        // wheel = zoom (default, explicit for clarity)
        zoomOnPinch
        zoomOnDoubleClick={false} // double-click is reserved for focusToolCall
        onNodeClick={handleNodeClick}
        onMoveStart={onDirectorMoveStart}  // TH-3b grace — user pan pauses the director
      >
        {/* TH-3c — floating replay transport; teardown centralized in ReplayContext.exit() (plan-triad B1) */}
        <ReplayTransport onExit={replay.exit} bufferedCount={bufferedCount} />
        <Background
          color={darkMode ? '#1f2937' : '#d1d5db'}
          gap={18}
          size={1}
        />
        <Controls
          position="bottom-right"
          showInteractive={false}
          className={darkMode ? '!bg-gray-900 !border-gray-700' : ''}
        />
        <MiniMap
          className="chatdag-minimap"
          pannable
          zoomable
          position="top-right"
          maskColor="rgba(2, 6, 23, 0.55)"
          maskStrokeColor="rgba(52, 211, 153, 0.85)"
          maskStrokeWidth={2}
          zoomStep={5}
          onNodeClick={(_e, node) => {
            // Phase-stack AABBs (and their collapsed-card form) navigate
            // exactly like the ControlsPanel rows: fitView the stack +
            // scroll the chat to the user bubble. Other minimap node
            // types (turn_marker / llm_round / tool_call / peer / fan
            // chips) are ignored here — they're individual elements,
            // not navigation targets.
            if (
              node.type !== 'phase_stack_group' &&
              node.type !== 'collapsed_phase_stack'
            ) {
              return;
            }
            const turnKey = (node.data as any)?.turnKey as string | undefined;
            if (!turnKey) return;
            const groupId =
              runtime.collapsedPhaseStackIds.has(turnKey) ||
              runtime.allPhaseStacksCollapsed
                ? `stack-${turnKey}`
                : `group-stack-${turnKey}`;
            try {
              fitView({
                nodes: [{ id: groupId }],
                padding: USER_FIT_PADDING,
                duration: USER_FIT_DURATION,
                maxZoom: USER_FIT_MAX_ZOOM,
              });
            } catch {
              /* node not rendered — no-op */
            }
            focusBubble(turnKey);
          }}
          // ReactFlow's MiniMap source extracts `style.background` and uses
          // it as the FILL of an inner <rect> drawn inside the SVG — but
          // ReactFlow's stylesheet ALSO sets `.react-flow__minimap {
          // background-color: #fff }` on the wrapper SVG. Without an
          // override that wrapper bg leaks through anywhere the inner
          // rect doesn't fully cover (rounded corners, alpha gaps), and
          // the alpha glass effect we set here renders white-tinted. The
          // chatdag-minimap class hooks an `index.css` override that
          // forces the wrapper to transparent, leaving the rect below as
          // the only bg — that's where this `style.background` lands.
          style={{
            background: darkMode
              ? 'rgba(11, 18, 32, 0.85)'
              : 'rgba(15, 23, 42, 0.82)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid ${darkMode ? 'rgba(52, 211, 153, 0.32)' : 'rgba(16, 185, 129, 0.38)'}`,
            borderRadius: 8,
            boxShadow:
              '0 4px 14px rgba(0, 0, 0, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            width: 140,
            height: 240,
            marginTop: 52,   // Cockpit Slice 1 — sit under the top-center command band
          }}
          nodeStrokeWidth={1.5}
          nodeStrokeColor="rgba(15, 23, 42, 0.85)"
          nodeBorderRadius={2}
          nodeColor={(n: Node) => {
            // P3 SP2 — ghosted nodes read gray in the minimap too.
            if ((n.data as any)?.offLens) {
              return darkMode ? 'rgba(107, 114, 128, 0.35)' : 'rgba(156, 163, 175, 0.4)';
            }
            switch (n.type) {
              case 'turn_marker':
                if ((n.data as any)?.role === 'user') {
                  return '#60a5fa';
                }
                return '#34d399';
              case 'llm_round':
                return (n.data as any)?.isSynthesis ? '#818cf8' : '#a78bfa';
              case 'peer':
                return '#94a3b8';
              case 'fan_collapsed_chip':
                return '#6b7280';
              case 'phase_stack_group': {
                // AABB fill mirrors PhaseStackGroupNode + ControlsPanel row
                // selection state. Realistic stack silhouette + color
                // identity = the panel/canvas/minimap all sing the same
                // tune for "this is where you are."
                const turnKey = (n.data as any)?.turnKey as string | undefined;
                if (turnKey && turnKey === runtime.errorTurnId) {
                  return 'rgba(244, 63, 94, 0.28)';        // rose
                }
                if (turnKey && turnKey === runtime.inFlightTurnId) {
                  return 'rgba(52, 211, 153, 0.30)';       // bright emerald
                }
                if (turnKey && turnKey === runtime.viewportTurnId) {
                  return 'rgba(52, 211, 153, 0.16)';       // dim emerald
                }
                return 'rgba(148, 163, 184, 0.10)';        // faint slate at rest
              }
              case 'collapsed_phase_stack':
                return '#9ca3af';
              // SP-2 — the dormant stem is a 1px chrome rail; keep it out of the
              // minimap (a stray purple speck via default otherwise). Collapsed
              // spine replies are hidden:true and RF11 already excludes them.
              case 'spine_lane_stem':
                return 'transparent';
              // SP-3a — the intent-chat panel reads amber in the minimap (the
              // blueprint-source accent), so the conversation block is locatable.
              case 'spine_chat_panel':
                return '#f59e0b';
              case 'turn_separator':
                // Very subtle so the chapter break appears in the
                // minimap as a faint horizontal tick instead of a
                // bright bar that competes with content nodes.
                return 'rgba(148, 163, 184, 0.22)';
              case 'tool_call':
              default:
                return '#c084fc';
            }
          }}
        />
        {/* Controls panel pinned top-right directly UNDER the MiniMap.
            marginTop offsets past the MiniMap's 240px height + its
            ~10px top inset + a small gap. */}
        {/* Trailmap L2 Drip 2 — grouping pill bar, top-left (ControlsPanel is
            top-right + 255 offset, so no collision). */}
        {/* Cockpit Slice 1 — HITL command band across the top-center of the ChatMap,
            gated on CouncilContext (returns null on non-council surfaces). The
            top-left / top-right overlays are marginTop-bumped to sit under it. */}
        <Panel position="top-center">
          <Cockpit sessionId={sessionId ?? ''} darkMode={darkMode} />
        </Panel>
        <Panel position="top-left" style={{ marginTop: 52 }}>
          <PillBar />
        </Panel>
        <Panel position="top-right" style={{ marginTop: 307 }}>
          <ControlsPanel
            onSummon={() => setSummonOpen(true)}
            blueprintBound={!!blueprintManifestId}
            onNewBlueprint={handleNewBlueprint}
            handlerRefs={handlerRefs}
            onCopyDebugSnapshot={handleCopyDebugSnapshot}
          />
        </Panel>
        {/* Trailmap L2 Drip 3 (DC-10) — the L1 corner ArtifactViewer panel was
            removed: artifacts now expand IN-CANVAS via ArtifactTileNode. */}
      </ReactFlow>

      {/* Living Blueprint SP1 — summon picker; onSummoned re-reads the binding
          so the flip card emits. */}
      <SummonBlueprintModal
        open={summonOpen}
        sessionId={sessionId ?? null}
        onClose={() => setSummonOpen(false)}
        onSummoned={handleSummoned}
      />

      {/* SP2b — editable genesis cost-gate. Opened by the blueprint card's
          seedling "Generate" / failure "Retry" via the runtime channel.
          onGenerating refetches the binding so the card flips to germinating. */}
      <BlueprintGenesisModal
        open={genesisModalOpen}
        sessionId={sessionId ?? null}
        intent={genesisIntent}
        onClose={() => setGenesisModalOpen(false)}
        onKeepRefining={() => setGenesisModalOpen(false)}
        onGenerating={() => { setGenesisModalOpen(false); refetchBlueprint(); }}
      />

      {/* Cockpit Slice 1 — the floating { } JSON debug button was relocated into
          the ControlsPanel ⋯ popover ("copy json snapshot") so it can't poke
          through the cockpit band's top-right. */}
    </div>
  );
};

export interface ChatDagCanvasProps {
  primaryPersona?: any;
  sessionId?: string;
  handlerRefs?: PanelHandlerRefs;
  greetingPending?: boolean;
}

export const ChatDagCanvas: React.FC<ChatDagCanvasProps> = ({
  primaryPersona,
  sessionId,
  handlerRefs,
  greetingPending,
}) => (
  <ReactFlowProvider>
    <ChatDagInner primaryPersona={primaryPersona} sessionId={sessionId} handlerRefs={handlerRefs} greetingPending={greetingPending} />
  </ReactFlowProvider>
);

export default ChatDagCanvas;
